/**
 * The full override tables, tucked away while a scope is active.
 * For Media Devoted | Created By Ciro Autiero
 *
 * In a scope view the one value above is the answer, so the multi-country
 * tables become noise — but they stay reachable, because assigning one number
 * to ten countries at once is still far faster there than ten scope switches.
 */

import { type FC, type ReactNode, useState } from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

export const AllScopesDisclosure: FC<{
  countryCount: number;
  campaignCount: number;
  children: ReactNode;
}> = ({ countryCount, campaignCount, children }) => {
  const [open, setOpen] = useState(false);
  const parts = [
    countryCount > 0 ? `${countryCount} ${countryCount === 1 ? 'country' : 'countries'}` : null,
    campaignCount > 0
      ? `${campaignCount} ${campaignCount === 1 ? 'campaign' : 'campaigns'}`
      : null,
  ].filter(Boolean);

  return (
    <div className="border-t border-border/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronRight
          className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-90')}
          aria-hidden
        />
        {/* "All scopes" was our word for it, not the reader's. */}
        <span className="font-medium">Other countries &amp; campaigns</span>
        <span className="truncate">
          {parts.length ? `(${parts.join(', ')})` : '(none set)'}
        </span>
      </button>
      {open ? <div>{children}</div> : null}
    </div>
  );
};
